import React, { Fragment } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { IFormatter } from '@kurtosys/types/data/IFormatter.js';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { factHasValue } from '../../../shared/factHasValue.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { AsOfDate } from '../../base/AsOfDate/AsOfDate.js';
import Image from '../../base/Image/Image.js';
import Link from '../../base/Link/Link.js';
import List from '../../base/List/List.js';
import MorningstarRating from '../../base/MorningStar/MorningStar.js';
import Tooltip from '../../base/Tooltip/Tooltip.js';
import { ITranslationContextValue } from '../../base/Translate/models/index.js';
import Translate, { TranslationContext } from '../../base/Translate/Translate.js';
import DocumentTiles from '../../file/DocumentTiles/DocumentTiles.js';
import ChangeIndicator from '../../overview/ChangeIndicator/ChangeIndicator.js';
import RiskRating from '../../risk/RiskRating/RiskRating.js';

import { IFactConfiguration, IFactProps, IFactState } from './models/index.js';
import { TTooltipElement } from './models/TTooltipElement.js';
import GroupListWrapper from './styledComponents/GroupListWrapper.js';
import GroupWrapper from './styledComponents/GroupWrapper.js';
import Subtitle from './styledComponents/Subtitle.js';
import Title from './styledComponents/Title.js';
import TitleWrapper from './styledComponents/TitleWrapper.js';
import Value from './styledComponents/Value.js';
import ValuePlaceholder from './styledComponents/ValuePlaceholder.js';
import ValueText from './styledComponents/ValueText.js';
import Wrapper from './styledComponents/Wrapper.js';
import { FACT_CATEGORY_KEY, FACT_COMPONENT_KEY } from './Fact.meta.js';

export class Fact extends ComponentBase<IFactProps, IFactState, IFactConfiguration> {
	categoryKey = FACT_CATEGORY_KEY;
	componentKey = FACT_COMPONENT_KEY;

	constructor(props: IFactProps) {
		super(props);

		makeObservable(this, {
			blankDataOptions: computed,
			hasTitle: computed,
			hasValue: computed,
			title: computed,
			subtitle: computed,
			placeholder: computed,
			changeIndicatorValue: computed,
			riskRatingValue: computed,
			morningstarRatingValue: computed,
			link: computed,
			appAsset: computed,
			documentTilesValue: computed,
			groupedListValue: computed,
			isGroupedList: computed,
		});
	}

	tooltip(element: TTooltipElement) {
		const { tooltip } = this.props;
		if (tooltip) {
			const tooltipProps = tooltip[element];
			return tooltipProps && <Tooltip {...tooltipProps} />;
		}
	}
	get blankDataOptions() {
		const { blankDataOptions = {} } = this.props;
		const defaultBlankDataOptions = {
			showBlank: true,
			placeholder: 'N/A',
		};
		return Object.assign(defaultBlankDataOptions, blankDataOptions);
	}
	get hasTitle() {
		const { title, subtitle } = this.props;
		return !common.commonUtils.isNullOrEmpty(title) || !common.commonUtils.isNullOrEmpty(subtitle);
	}
	get hasValue() {
		return factHasValue(this.props);
	}
	get title() {
		const { title = '' } = this.props;
		return (
			<Title>
				<Translate>{title}</Translate>
				{this.tooltip('title')}
			</Title>
		);
	}

	get subtitle() {
		const { subtitle = '' } = this.props;
		return (
			<Subtitle>
				{typeof subtitle === 'string' ? <Translate>{subtitle}</Translate> : <AsOfDate {...subtitle} />}
				{this.tooltip('subtitle')}
			</Subtitle>
		);
	}

	get placeholder() {
		const { placeholder } = this.blankDataOptions;
		return (
			<ValuePlaceholder>
				<Translate>{placeholder}</Translate>
			</ValuePlaceholder>
		);
	}
	get changeIndicatorValue() {
		const { changeIndicatorProps } = this.props;
		return <ChangeIndicator {...changeIndicatorProps} />;
	}
	get riskRatingValue() {
		const { riskRatingProps } = this.props;
		return <RiskRating {...riskRatingProps} />;
	}
	get morningstarRatingValue() {
		const { morningstarRatingProps } = this.props;
		return <MorningstarRating {...morningstarRatingProps} />;
	}

	get link() {
		const { linkProps } = this.props;
		if (linkProps) {
			return <Link {...linkProps} />;
		}
	}

	get appAsset() {
		const { appAssetProps } = this.props;
		return <Image {...appAssetProps} />;
	}

	get documentTilesValue() {
		const { documentTilesProps, onDocumentClick } = this.props;
		return <DocumentTiles {...documentTilesProps} onDocumentClick={onDocumentClick} />;
	}

	get groupedListValue() {
		const { groupedListProps } = this.props;
		if (groupedListProps && groupedListProps.items && Array.isArray(groupedListProps.items)) {
			return (
				<List
					items={groupedListProps.items.map((fact, index) => {
						return <Fact key={index} {...fact} />;
					})}
				/>
			);
		}
	}

	get isGroupedList(): boolean {
		const { dataType } = this.props;
		return dataType === 'grouped-list';
	}

	getValueText = (translationContext: ITranslationContextValue) => {
		const { format, listValueSeparator = ', ' } = this.props;
		let { value } = this.props;
		if (Array.isArray(value)) {
			value = value.join(listValueSeparator);
		}
		if (format && value) {
			format.locale = format.locale || (translationContext && translationContext.culture);
			format.options = format.options || {};
			value = common.commonUtils.formatter(format as IFormatter, value) as string;
		}
		return <ValueText>{value}</ValueText>;
	};
	getValue(translationContext: ITranslationContextValue) {
		const { dataType } = this.props;
		if (this.hasValue) {
			if (dataType === 'change-indicator') {
				return this.changeIndicatorValue;
			}
			if (dataType === 'risk-rating') {
				return this.riskRatingValue;
			}
			if (dataType === 'morningstar-rating') {
				return this.morningstarRatingValue;
			}
			if (dataType === 'app-asset') {
				return this.appAsset;
			}
			if (dataType === 'document-tiles') {
				return this.documentTilesValue;
			}
			if (dataType === 'link') {
				return this.link;
			}
		}
		if (dataType === 'grouped-list') {
			return this.groupedListValue;
		}
		return this.hasValue ? this.getValueText(translationContext) : this.placeholder;
	}

	renderValue(translationContext: ITranslationContextValue) {
		return (
			<Value data-has-title={this.hasTitle} data-has-grouped-list={this.isGroupedList}>
				{this.getValue(translationContext)}
				{this.tooltip('value')}
			</Value>
		);
	}

	render() {
		const { className, hide } = this.props;
		const { showBlank } = this.blankDataOptions;

		if ((!this.hasValue && !showBlank) || hide) {
			return null;
		}

		// eslint-disable-next-line @typescript-eslint/naming-convention
		const FactWrapper = this.isGroupedList ? GroupWrapper : Fragment;

		return (
			<Wrapper className={className} role="group">
				<TranslationContext.Consumer>
					{(translationContext) => {
						return (
							<FactWrapper>
								{this.hasTitle && (
									<TitleWrapper>
										{this.title}
										{this.subtitle}
									</TitleWrapper>
								)}
								{this.isGroupedList && (
									<GroupListWrapper>{this.renderValue(translationContext)}</GroupListWrapper>
								)}
								{!this.isGroupedList && this.renderValue(translationContext)}
							</FactWrapper>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Fact), FACT_CATEGORY_KEY, FACT_COMPONENT_KEY);
